import { computed, ref } from 'vue';
import utilities from '@/shared/utilities.service';
import {
    AltaPayPaymentInitializeResult,
    B2CClientService, BasketCheckoutModel as B2CBasketCheckoutModel,
    FinalizeAltaPayPaymentRequest,
} from '@/b2c/shared/api/server-apis';
import {
    B2BClientService, BasketCheckoutModel as B2BBasketCheckoutModel, PaymentOptionType,
} from '@/b2b/shared/api/server-apis';

import useB2CBasket from '@/b2c/basket/useBasket';
import useB2BBasket from '@/b2b/basket/useBasket';
import { useCheckout as useB2CCheckout } from '@/b2c/checkout/useCheckout';
import { useCheckout as useB2BCheckout } from '@/b2b/checkout/useCheckout';
import { usePaymentOptions } from '@/shared/checkout/usePaymentOptions';
import { useRouteQuery } from '@/core/browser/route-query.composable';
import { QueryKeys } from '@/shared/config/route-query.config';
import Cookies from 'js-cookie';
import Deferred from '@/core/async/Deferred';
import { useNewsletterSubscription } from '@/b2c/checkout/useNewsletterSubscription';
import useAltaPayPaymentMethods from '@/shared/checkout/altapay/useAltaPayPaymentMethods';
import basketTrackingService from '@/shared/tracking/basket-tracking.service';
import authStore from '@/shared/auth/auth.store';

export type StringBoolean = 'true' | 'false';

export type AltaPayEvent = {
    type: string;
    paymentId: string;
    shopOrderId: string;
    status: string;
};

export type Callback = (event: AltaPayEvent) => Promise<void> | void;

export type Callbacks = {
    [key: string]: Callback;
}

export type CallbacksBuilder = {
    setSuccess: (callback: Callback) => CallbacksBuilder;
    setFailure: (callback: Callback) => CallbacksBuilder;
    setRedirectUrl: (url: string) => CallbacksBuilder;
    setFormStylingUrl: (url: string) => CallbacksBuilder;
    build: () => Callbacks;
}

export type PaymentMethod = {
    name: string;
    description: string;
    id: string;
    logoUrl: string;
    paymentMethodMetadata: {
        canIssueNewCredit: StringBoolean;
        canUseCredit: StringBoolean;
        merchantId?: string;
        terminalName: string;
    },
};

export type RenderPaymentMethodEvent = {
    paymentMethod: PaymentMethod,
    defaultPaymentMethodElement: HTMLElement,
    onSelectPaymentMethodCallback: () => void
};

export type RenderPaymentMethodCallback = (event: RenderPaymentMethodEvent) => HTMLElement | null;

export interface IAltaPay {
    init: (token: string, sessionId: string) => IAltaPay;
    setCallbacks: (Callbacks) => IAltaPay;
    handleRedirectData: (string) => Promise<IAltaPay>;
    initiatePayment: () => IAltaPay;
    injectPaymentMethods: (element: HTMLElement) => Promise<IAltaPay>;
    onRenderPaymentMethodCallback: (callback: RenderPaymentMethodCallback) => IAltaPay;
    sessionToken: string;
    reset: () => void;
    closePayment: () => void;
}

export interface IAltaPayFactory {
    getCallbacksBuilder: () => CallbacksBuilder;
}

declare global {
    interface Window {
        Altapay?: IAltaPay;
        AltaPayFactory?: IAltaPayFactory;
    }
}

let session: null | IAltaPay = null;
const initializing = ref<boolean>(false);
const initialized = ref<boolean>(false);
const isHandlingRedirectData = ref<boolean>(false);
const isB2B = computed<boolean>(() => authStore.isSignedInAsPartner);

export const ALTAPAY_PAYMENT_TOKEN_COOKIE_NAME = 'altaPayPaymentToken';

export default function useAltaPay() {
    const ClientService = isB2B.value ? B2BClientService : B2CClientService;
    const useBasket = isB2B.value ? useB2BBasket : useB2CBasket;
    const useCheckout = isB2B.value ? useB2BCheckout : useB2CCheckout;

    const redirectSessionId = useRouteQuery<string | undefined>(QueryKeys.AltaPaySessionId);
    const redirectData = useRouteQuery<string | undefined>(QueryKeys.AltaPayRedirectData);

    const { basketId } = useBasket();
    const { redirectToReceipt, basket } = useCheckout();
    const { saveSubscriptionForRedirectData, getSubscriptionForRedirectData, removeSubscriptionForRedirectData } = useNewsletterSubscription();
    const { isAltaPayProviderSelected, clearSelectedProvider, setProviderError } = usePaymentOptions();
    const { renderPaymentMethods, clearSelectedPaymentMethod, paymentMethodsContainer } = useAltaPayPaymentMethods();

    const init = async(formStylingUrl: string, shop?: string) => {
        const isHandling = await maybeHandleRedirectData(formStylingUrl);
        if (isHandling) {
            return;
        }
        resetProvider();
        try {
            const paymentContext = await getPaymentContext(shop);
            if (paymentContext?.token && paymentContext?.sessionId) {
                await initAltaPaySDK(paymentContext.token, paymentContext.sessionId, formStylingUrl);
            }
        } catch (e) {
            onPaymentFailure();
        }
    };

    const getPaymentContext = async(shopName?: string): Promise<AltaPayPaymentInitializeResult | undefined> => {
        return await ClientService.initializeAltaPayPayment(basketId.value, { shopName })
            .then(utilities.unwrapModel);
    };

    const initAltaPaySDK = async(token: string, id: string, formStylingUrl: string) => {
        if (!window.Altapay || !window.AltaPayFactory) {
            return;
        }
        initializing.value = true;
        session = await window.Altapay
            .init(token, id)
            .setCallbacks(
                window.AltaPayFactory.getCallbacksBuilder()
                    .setSuccess(onPaymentSuccess)
                    .setFailure(onPaymentFailure)
                    .setFormStylingUrl(formStylingUrl)
                    .setRedirectUrl(window.location.href)
                    .build())
            .onRenderPaymentMethodCallback(renderPaymentMethods)
            .injectPaymentMethods(paymentMethodsContainer.value as HTMLElement);
        initializing.value = false;
    };

    const finalizePayment = async(paymentId: string, orderNumber: string, subscribeNewsletter: boolean): Promise<string | undefined> => {
        const request: FinalizeAltaPayPaymentRequest = {
            paymentId,
            orderNumber,
            subscribeNewsletter,
        };
        const response = await ClientService.finalizeAltaPayPayment(basketId.value, request)
            .then(utilities.unwrapModel);
        return response !== undefined ? response.orderNumber : undefined;
    };

    const onPaymentSuccess = async(event: AltaPayEvent) => {
        const subscription = getSubscriptionForRedirectData();
        try {
            const orderNumber = await finalizePayment(event.paymentId, event.shopOrderId, subscription);
            if (orderNumber) {
                await redirectToReceipt(orderNumber);
            }
            resetPayment();
        } catch (e) {
            onPaymentFailure();
        }
    };

    const onPaymentFailure = () => {
        setProviderError();
        resetPayment();
    };

    const initiatePayment = async() => {
        if (session) {
            saveTokenForRedirectData(session.sessionToken);
            saveSubscriptionForRedirectData();
            try {
                await session.initiatePayment();
                !isB2B.value && basketTrackingService.addPaymentInfo(basket.value as unknown as B2CBasketCheckoutModel);
            } catch (e) {
                onPaymentFailure();
            }
        }
    };

    const maybeHandleRedirectData = async(formStylingUrl: string) => {
        const deferred = new Deferred<boolean>();
        const token = getTokenForRedirectData();
        if (token && redirectData.value && redirectSessionId.value) {
            deferred.resolve(true);
            isHandlingRedirectData.value = true;
            await initAltaPaySDK(token, redirectSessionId.value, formStylingUrl);
            if (session) {
                await session.handleRedirectData(redirectData.value);
            }
        } else {
            deferred.resolve(false);
        }
        return deferred.promise;
    };

    const resetRedirectData = () => {
        removeTokenForRedirectData();
        removeSubscriptionForRedirectData();
        redirectData.value = undefined;
        redirectSessionId.value = undefined;
        isHandlingRedirectData.value = false;
    };

    const resetPayment = () => {
        session && session.reset();
        resetProvider();
        resetRedirectData();
    };

    const resetProvider = () => {
        clearSelectedPaymentMethod();
        isAltaPayProviderSelected.value && clearSelectedProvider();
    };

    const saveTokenForRedirectData = (token: string) => {
        Cookies.set(ALTAPAY_PAYMENT_TOKEN_COOKIE_NAME, token);
    };

    const getTokenForRedirectData = (): string | undefined => {
        return Cookies.get(ALTAPAY_PAYMENT_TOKEN_COOKIE_NAME);
    };

    const removeTokenForRedirectData = () => {
        Cookies.remove(ALTAPAY_PAYMENT_TOKEN_COOKIE_NAME);
    };

    const setInitialized = (isInitialized: boolean) => {
        initialized.value = isInitialized;
    };

    const isAllowedOnlinePayment = computed(() => {
        const paymentOptions = (basket.value as unknown as B2BBasketCheckoutModel)?.paymentOptions;
        if (paymentOptions) {
            const onlinePaymentOption = paymentOptions.find(option => option.type === PaymentOptionType.Online);
            return onlinePaymentOption !== undefined;
        } else {
            return true;
        }
    });

    return {
        initializing,
        initialized,
        isHandlingRedirectData,
        isAllowedOnlinePayment,
        init,
        setInitialized,
        initiatePayment,
        resetPayment,
    };
}

// Info for develoment and debugging

// import siteContextStore from '@/shared/content/site-context.store';
// function getDevelopFormStylingUrl() {
//     const origin = 'https://c6b512f1646a.ngrok.app';
//     const culture = siteContextStore.settings.language.toLowerCase();
//     const page = 'altapay-checkout-form';
//     return `${origin}/${culture}/${page}`;
// }

// 4111000000000511
// https://developer.mobilepay.dk/docs/pos/development-guide/test
// ngrok http --region=eu --bind-tls=true  --host-header=www.bolia.test https://www.bolia.test:443
// /da-dk/altapay-checkout-form
// \src\Bolia.Website\Views\AltaPayCheckout\Index.cshtml

// Klarna findes i 2 udgaver (terminal id'er) -> skulle også virke for DKK
// Bolia.com Klarna Account DKK
// Bolia.com Klarna Invoice DKK

// Paypal skulle også virke for DK "Bolia.com PayPal Test Terminal"
// Vipps skulle virke for Norge "Bolia.com Vipps Test Terminal"
// Swish virker i Sverige "Bolia.com Swish SEK"
// Ideal virker for Holland "Bolia Ideal Test Terminal"

// Og så er der jo apple pay

// Terminal accepted currencies
// Bolia CheckoutAPI ApplePay Test Terminal - DKK,CHF,EUR,GBP,NOK,SEK
// Bolia CheckoutAPI Klarna (Account, Invoice) Test Terminal - EUR,NOK,SEK
// Bolia CheckoutAPI MobilePay Test Terminal - DKK
// Bolia CheckoutAPI PayPal Test Terminal - EUR
// Bolia CheckoutAPI Swish Test Terminal - SEK
// Bolia CheckoutAPI Test Terminal - ANY
// Bolia CheckoutAPI Vipps Test Terminal - NOK

// Bolia CheckoutAPI Bancontact Test Terminal - Belgium
// Bolia CheckoutAPI iDeal Test Terminal - Netherlands
